import { DocumentMapping } from "@/configuration/document-mappings/types";
import { Classification } from "@/src/models/constants/classifications";

export const VPS_EMAIL_FOLDER_SENDERS: ReadonlySet<string> = new Set([
  "carly.price@vnahg.org",
  "jamilet.balzano@vnahg.org",
  "renee.baez@vnahg.org",
]);
export const VPS_EMAIL_FOLDER_BY_SUBJECT: Readonly<Record<string, string>> = {
  "scan aco apcm consent": "ACO/APCM Consent",
  "scan blank forms": "Blank Forms",
  "scan clinical images": "Clinical Images",
  "scan death cert": "Death Certificates",
  "scan dme": "DME/CMN-Misc",
  "scan ext med rec": "External Medical Records",
  "scan financial docs": "Financial Documents",
  "scan immunization consent": "Immunization Consent",
  "scan labs": "Lab Results VPS",
  "scan md hospital records": "MD/Hospital Records",
  "scan meds": "Medications VPS",
  "scan other": "Other VPS",
  "scan poa": "Power Of Attorney",
  "scan polst": "POLST VPS",
  "scan pt auth": "Patient Authorization",
  "scan social determinants": "Social Determinants Of Helath",
  "scan surgery clearance": "Surgery Clearance - VPS",
  "scan xray": "X-Ray Diagnostic Results VPS",
};

export const VPS_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.ADMIN_ACO]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd ACO Consent signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "ACO - No Sig/Action Required",
    documentLabel: "{{ dateSigned }} ACO Consent - {{ patientInitials }}",
    folder: "ACO/APCM Consent",
    folderMapping: "87161fd7-d67f-4f10-ae98-2b36a5f27685",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_ACO,
    group: "vps",
  },
  [Classification.ADMIN_APCM]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd APCM Consent signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "APCM - No Sig/Action Required",
    documentLabel: "{{ dateSigned }} APCM Consent - {{ patientInitials }}",
    folder: "ACO/APCM Consent",
    folderMapping: "87161fd7-d67f-4f10-ae98-2b36a5f27685",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_APCM,
    group: "vps",
  },
  [Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateReceived }} {{ companyName }} CMN"],
    assignedTo: null,
    displayName: "CMN - No Sig/Action Required",
    documentLabel: "{{ companyName }}_{{ dateReceived }}_CMN_{{ patientInitials }}",
    folder: "DME/CMN-Misc",
    folderMapping: "4232e93f-e08a-4efa-9b3a-e74eeed9e14d",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY,
    group: "vps",
  },
  [Classification.ADMIN_DEATH_CERTIFICATES]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd Death Cert {{ dateOfEvent }}"],
    assignedTo: null,
    displayName: "Death Certificates",
    documentLabel: "{{ dateOfEvent }} Death Certificate - {{ patientInitials }}",
    folder: "Death Certificates",
    folderMapping: "cb7883e2-877d-44be-a782-cb12ecc10916",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_DEATH_CERTIFICATES,
    group: "vps",
  },
  [Classification.ADMIN_DISABILITY_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd signed disability form"],
    assignedTo: null,
    displayName: "Disability Forms - No Sig/Action Required",
    documentLabel: "{{ dateSigned }} Disability Forms - {{ patientInitials }}",
    folder: "Other VPS",
    folderMapping: "d40a7a1a-8f4d-4125-bce4-226cc227105f",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_DISABILITY_FORMS,
    group: "vps",
  },
  [Classification.ADMIN_FMLA_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd FMLA form signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "FMLA - No Sig/Action Required",
    documentLabel: "{{ dateSigned }} FMLA Forms - {{ patientInitials }}",
    folder: "Other VPS",
    folderMapping: "d40a7a1a-8f4d-4125-bce4-226cc227105f",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_FMLA_FORMS,
    group: "vps",
  },
  [Classification.ADMIN_OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd signed {{ documentType }} ({{ companyName }})"],
    assignedTo: null,
    displayName: "Admin - No Sig/Action Required",
    documentLabel: "{{ dateReceived }} {{ companyName }} {{ documentType }} - {{ patientInitials }}",
    folder: "Other VPS",
    folderMapping: "d40a7a1a-8f4d-4125-bce4-226cc227105f",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_OTHER,
    group: "vps",
  },
  [Classification.ADMIN_PATIENT_AUTHORIZATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd Patient Auth signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "Patient Authorization - No Sig/Action Required",
    documentLabel: "{{ dateSigned }} Pt Auth - {{ patientInitials }}",
    folder: "Patient Authorization",
    folderMapping: "1191e873-edb0-4c08-a578-f5793e22011a",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_PATIENT_AUTHORIZATION,
    group: "vps",
  },
  [Classification.ADMIN_POLST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd POLST signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "POLST - No Sig/Action Required",
    documentLabel: "{{ dateSigned }} POLST Forms - {{ patientInitials }}",
    folder: "POLST VPS",
    folderMapping: "fe4fcd04-beb2-4263-8e00-c34d708c38a8",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_POLST,
    group: "vps",
  },
  [Classification.ADMIN_RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Medical record not found at {{ clinicName }}"],
    assignedTo: null,
    displayName: "Medical records from MD/hospital",
    documentLabel: "{{ dateOfEvent }} {{ specialtyFolder }} Record Not Found - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND,
    group: "vps",
  },
  [Classification.ADMIN_SOCIAL_DETERMINANTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd social determinants form signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "Social Determinants - No Sig/Action Required",
    documentLabel: "{{ dateSigned }} Social Determinants - {{ patientInitials }}",
    folder: "Social Determinants of Helath",
    folderMapping: "0c6df3b0-0ece-438a-9e37-d0d63ca38f7b",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_SOCIAL_DETERMINANTS,
    group: "vps",
  },
  [Classification.ADMIN_TRANSPORT_FORM]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Transport Form",
    documentLabel: "{{ dateReceived }} {{ companyName }} Transport Form - {{ patientInitials }}",
    folder: "DME/CMN-Misc",
    folderMapping: "4232e93f-e08a-4efa-9b3a-e74eeed9e14d",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.ADMIN_TRANSPORT_FORM,
    group: "vps",
  },
  [Classification.BLANK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd unsigned {{ documentType }} from {{ companyName }}; sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Blank forms",
    documentLabel: "{{ dateReceived }} {{ companyName }} {{ documentType }} Sig Reqd - {{ patientInitials }}",
    folder: "Blank Forms",
    folderMapping: "28c41063-ca85-4119-aab5-06e2e5e7a713",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.BLANK,
    group: "vps",
  },
  [Classification.CARE_PLAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} POC recvd {{ companyName }}"],
    assignedTo: null,
    displayName: "POC - No Signature/Action Required",
    documentLabel: "{{ companyName }}_{{ dateOfEvent }}_POC_{{ patientInitials }}",
    folder: "External Medical Records",
    folderMapping: "3be3f736-3fcc-4f2b-9849-550d456c2899",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.CARE_PLAN,
    group: "vps",
  },
  [Classification.CLEARANCE_REQUEST_SURGICAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd {{ schedulingPriority }} Surgery clearance; sent to PCP {{ familyName }}"],
    assignedTo: null,
    displayName: "Surgery clearance request",
    documentLabel: "{{ dateReceived }} {{ schedulingPriority }} Surgery Clearance - {{ patientInitials }}",
    folder: "Surgery Clearance - VPS",
    folderMapping: "0d56ab65-d335-408a-b6e7-f9a21aa64c61",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.CLEARANCE_REQUEST_SURGICAL,
    group: "vps",
  },
  [Classification.DME_ORDER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} {{ companyName }} DME"],
    assignedTo: null,
    displayName: "DME - No Signature/Action Required",
    documentLabel: "{{ companyName }}_{{ dateReceived }}_DME_{{ patientInitials }}",
    folder: "DME/CMN-Misc",
    folderMapping: "4232e93f-e08a-4efa-9b3a-e74eeed9e14d",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.DME_ORDER,
    group: "vps",
  },
  [Classification.FINANCIAL_BILLING_RECORDS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd billing records"],
    assignedTo: null,
    displayName: "Financial documents",
    documentLabel: "{{ dateReceived }} Billing Records - {{ patientInitials }}",
    folder: "Financial Documents",
    folderMapping: "4d601c3d-f7a1-4ffd-976d-ad179928a0aa",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.FINANCIAL_BILLING_RECORDS,
    group: "vps",
  },
  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd EOB"],
    assignedTo: null,
    displayName: "Coverage and Benefits Eligibility",
    documentLabel: "{{ dateReceived }} EOB - {{ patientInitials }}",
    folder: "Other VPS",
    folderMapping: "d40a7a1a-8f4d-4125-bce4-226cc227105f",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_CLINICAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Clinical images sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Clinical Imaging",
    documentLabel: "{{ dateOfEvent }} Clinical Images {{ bodyPart }} - {{ patientInitials }}",
    folder: "Clinical Images",
    folderMapping: "c826e34f-86e3-4e9d-8419-730ebe0bdfb8",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_CLINICAL,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_COLONOSCOPY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Colonoscopy perf {{ dateOfEvent}} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Colonoscopy {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_COLONOSCOPY,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_CT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ bodyPart }} CT perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} CT {{ bodyPart }} {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_CT,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_DEXA]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["DEXA perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} DEXA {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_DEXA,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_ECHO]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Echo perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} ECHO {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_ECHO,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_EEG]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["EEG perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} EEG {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_EEG,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_EKG]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["EKG perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} EKG {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_EKG,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_HOLTER_MONITOR]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Holter perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Holter Monitor {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_HOLTER_MONITOR,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_MAMMOGRAM]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Mammo perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Mammo {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_MAMMOGRAM,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_MRI]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ bodyPart }} MRI perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} MRI {{ bodyPart }} {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_MRI,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_NEEDS_CLARIFICATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ imagingType }} needs clarification; sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Clarification",
    documentLabel: "{{ dateReceived }} {{ imagingType }} Clarification Needed - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_NEEDS_CLARIFICATION,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_NEEDS_SIGNATURE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ imagingType }} needs sig; sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Clarification",
    documentLabel: "{{ dateReceived }} {{ imagingType }} Sig Reqd - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_NEEDS_SIGNATURE,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_NM]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["NM {{ studyType }} perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} NM {{ studyType }} {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_NM,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ imagingType }} perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} {{ imagingType }} {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_OTHER,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_PET]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ bodyPart }} perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} PET {{ bodyPart }} {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_PET,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_PULMONARY_FUNCTION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Spirometry perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Pulmonary Function {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_PULMONARY_FUNCTION,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_RETINAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Retinal perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Retinal {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_RETINAL,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_STRESS_TEST_EXERCISE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Stress test perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Exercise Stress Test {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_STRESS_TEST_EXERCISE,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_STRESS_TEST_NM]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["NM {{ studyType }} perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} NM Stress Test {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_STRESS_TEST_NM,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_ULTRASOUND]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ bodyPart }} US perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Ultrasound {{ bodyPart }} {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_ULTRASOUND,
    group: "vps",
  },
  [Classification.IMAGING_RESULTS_X_RAY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ bodyPart }} X-Ray perf {{ dateOfEvent }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Diagnostic Imaging Results",
    documentLabel: "{{ dateOfEvent }} Xray {{ bodyPart }} {{ significantFindings }} - {{ patientInitials }}",
    folder: "X-Ray Diagnostic Results VPS",
    folderMapping: "a6227bc0-572c-48be-ace7-033898b5aa5b",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.IMAGING_RESULTS_X_RAY,
    group: "vps",
  },
  [Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Missing PHI",
    documentLabel: "{{ dateReceived }} Missing PHI",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email",
    classification: Classification.INCOMPLETE_CANNOT_LINK_TO_PATIENT,
    group: "vps",
  },
  [Classification.INCOMPLETE_ILLEGIBLE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Illegible Fax",
    documentLabel: "{{ dateReceived }} Illegible Fax - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email",
    classification: Classification.INCOMPLETE_ILLEGIBLE,
    group: "vps",
  },
  [Classification.INCOMPLETE_NOT_ALL_PAGES]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Incomplete Fax",
    documentLabel: "{{ dateReceived }} Incomplete Fax - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email",
    classification: Classification.INCOMPLETE_NOT_ALL_PAGES,
    group: "vps",
  },
  [Classification.INTAKE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["FALSE"],
    assignedTo: null,
    displayName: "New patient (intakes/referrals)",
    documentLabel: "{{ dateReceived }} Intake - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email only",
    classification: Classification.INTAKE,
    group: "vps",
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["FALSE"],
    assignedTo: null,
    displayName: "Solicitations/Ads",
    documentLabel: "",
    folder: null,
    folderMapping: null,
    inputDate: "FALSE",
    instructions: null,
    workflowAction: null,
    workflowType: "",
    classification: Classification.JUNK,
    group: "vps",
  },
  [Classification.LAB_RESULTS_A1C]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} A1C sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} A1C - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_A1C,
    group: "vps",
  },
  [Classification.LAB_RESULTS_CBC]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} CBC sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} CBC - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_CBC,
    group: "vps",
  },
  [Classification.LAB_RESULTS_CMP]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} CMP sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} CMP - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_CMP,
    group: "vps",
  },
  [Classification.LAB_RESULTS_COLOGUARD]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} cologuard sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} Cologuard - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_COLOGUARD,
    group: "vps",
  },
  [Classification.LAB_RESULTS_DRUG_SCREEN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} drug screen sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} Drug Screen - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_DRUG_SCREEN,
    group: "vps",
  },
  [Classification.LAB_RESULTS_FIT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} FIT sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} FIT - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_FIT,
    group: "vps",
  },
  [Classification.LAB_RESULTS_LIPID_PANEL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} lipids sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} Lipids - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_LIPID_PANEL,
    group: "vps",
  },
  [Classification.LAB_RESULTS_MULTIPLE_LABS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} labs sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} {{ nameOfLab }}, {{ nameOfLab }} - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_MULTIPLE_LABS,
    group: "vps",
  },
  [Classification.LAB_RESULTS_ORDER_FORM]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} {{ nameOfLab }} order sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateReceived }} {{ nameOfLab }} Lab Order {{ signatureRequired }} - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_ORDER_FORM,
    group: "vps",
  },
  [Classification.LAB_RESULTS_OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} {{ nameOfLab }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} {{ nameOfLab }} - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_OTHER,
    group: "vps",
  },
  [Classification.LAB_RESULTS_PAP_SMEAR]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} pap sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} Pap Smear - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_PAP_SMEAR,
    group: "vps",
  },
  [Classification.LAB_RESULTS_PATHOLOGY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} path/biopsy sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} {{ bodyPart }} Pathology/Biopsy - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_PATHOLOGY,
    group: "vps",
  },
  [Classification.LAB_RESULTS_PT_INR]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} PT-INR sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} PT INR - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_PT_INR,
    group: "vps",
  },
  [Classification.LAB_RESULTS_TEST_NOT_PERFORMED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} {{ nameOfLab }} Not Performed"],
    assignedTo: null,
    displayName: "lab_results_test_not_performed",
    documentLabel: "{{ dateReceived }} Test Not Performed - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_TEST_NOT_PERFORMED,
    group: "vps",
  },
  [Classification.LAB_RESULTS_URINALYSIS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} {{ companyName }} urinalysis sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Lab Results",
    documentLabel: "{{ dateOfEvent }} Urinalysis - {{ patientInitials }}",
    folder: "Lab Results VPS",
    folderMapping: "f922e514-1b8f-48fa-908f-46bb8ca73b1e",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.LAB_RESULTS_URINALYSIS,
    group: "vps",
  },
  [Classification.LEGAL_ADVANCED_CARE_DIRECTIVE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd Advance Care Directive signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "Advance Directives",
    documentLabel: "{{ dateSigned }} Advance Care Directive - {{ patientInitials }}",
    folder: "POLST VPS",
    folderMapping: "fe4fcd04-beb2-4263-8e00-c34d708c38a8",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.LEGAL_ADVANCED_CARE_DIRECTIVE,
    group: "vps",
  },
  [Classification.LEGAL_POWER_OF_ATTORNEY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd Power of Attorney document signed {{ dateSigned }}"],
    assignedTo: null,
    displayName: "Power of Attorney (POA)",
    documentLabel: "{{ dateSigned }} POA - {{ patientInitials }}",
    folder: "Power Of Attorney",
    folderMapping: "12825250-ddb7-401c-b7ea-ebbb72c8b132",
    inputDate: "Date Signed",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.LEGAL_POWER_OF_ATTORNEY,
    group: "vps",
  },
  [Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd medical records from {{ clinicName }}"],
    assignedTo: null,
    displayName: "Bulk medical records",
    documentLabel: "{{ dateReceived }} {{ clinicName }} Records - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC,
    group: "vps",
  },
  [Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd medical records from {{ clinicName }}"],
    assignedTo: null,
    displayName: "Bulk medical records",
    documentLabel: "{{ dateReceived }} {{ clinicName }} Records - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL,
    group: "vps",
  },
  [Classification.MEDICAL_HISTORY_HOME_HEALTH]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} {{ documentType }} recvd {{ companyName }}"],
    assignedTo: null,
    displayName: "Notes from HH/hospice/PT/rehab - No Sig/Action Required",
    documentLabel: "{{ companyName }}_{{ dateOfEvent }}_{{ documentType }}_{{ patientInitials }}",
    folder: "External Medical Records",
    folderMapping: "3be3f736-3fcc-4f2b-9849-550d456c2899",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.MEDICAL_HISTORY_HOME_HEALTH,
    group: "vps",
  },
  [Classification.MEDICAL_HISTORY_IMMUNIZATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ vaccineType }} immunization record recvd"],
    assignedTo: null,
    displayName: "Immunizations",
    documentLabel: "{{ dateOfEvent }} Iz {{ vaccineType }} - {{ patientInitials }}",
    folder: "Immunization Consent",
    folderMapping: "d032c37f-63d1-49db-96cc-927621f0775f",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.MEDICAL_HISTORY_IMMUNIZATION,
    group: "vps",
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd med rec req from {{ companyName }}, sent to Brittany"],
    assignedTo: null,
    displayName: "Med record request",
    documentLabel: "{{ dateReceived }} Med Records Req - {{ patientInitials }}",
    folder: "Other VPS",
    folderMapping: "d40a7a1a-8f4d-4125-bce4-226cc227105f",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: "vps",
  },
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["FALSE"],
    assignedTo: null,
    displayName: "Med record request",
    documentLabel: "FALSE - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email only",
    classification: Classification.MEDICAL_RECORD_REQUEST_HEDIS,
    group: "vps",
  },
  [Classification.NOTIFICATION_APPOINTMENT_CANCELLED_MISSED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["recvd appt cancelled/missed notification"],
    assignedTo: null,
    displayName: "notification_appointment_cancelled_missed",
    documentLabel: "{{ dateReceived }} Appt Cancelled/Missed - {{ patientInitials }}",
    folder: "External Medical Records",
    folderMapping: "3be3f736-3fcc-4f2b-9849-550d456c2899",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.NOTIFICATION_APPOINTMENT_CANCELLED_MISSED,
    group: "vps",
  },
  [Classification.NOTIFICATION_HOSPITAL_ADMIT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} Hosp admit, sent to Patricia"],
    assignedTo: null,
    displayName: "Hospital Admit Notice",
    documentLabel: "{{ dateOfEvent }} Hospital Admit - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.NOTIFICATION_HOSPITAL_ADMIT,
    group: "vps",
  },
  [Classification.NOTIFICATION_HOSPITAL_DISCHARGE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} Hosp DC, sent to Patricia"],
    assignedTo: null,
    displayName: "Hospital Discharge Notice",
    documentLabel: "{{ dateOfEvent }} Hospital DC - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.NOTIFICATION_HOSPITAL_DISCHARGE,
    group: "vps",
  },
  [Classification.NOTIFICATION_HOSPITAL_ER_ADMIT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} ER admit, sent to Patricia"],
    assignedTo: null,
    displayName: "ER Admit Notice",
    documentLabel: "{{ dateOfEvent }} ER Admit - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.NOTIFICATION_HOSPITAL_ER_ADMIT,
    group: "vps",
  },
  [Classification.NOTIFICATION_HOSPITAL_ER_DISCHARGE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} ER DC, sent to Patricia"],
    assignedTo: null,
    displayName: "ER Discharge Notice",
    documentLabel: "{{ dateOfEvent }} ER DC - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.NOTIFICATION_HOSPITAL_ER_DISCHARGE,
    group: "vps",
  },
  [Classification.NOTIFICATION_HOSPITAL_SDS_ADMIT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} Surgery admit, sent to Patricia"],
    assignedTo: null,
    displayName: "Procedure/Surgical Admit Notice",
    documentLabel: "{{ dateOfEvent }} Day Surgery Admit - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.NOTIFICATION_HOSPITAL_SDS_ADMIT,
    group: "vps",
  },
  [Classification.NOTIFICATION_HOSPITAL_SDS_DISCHARGE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} Surgery DC, sent to Patricia"],
    assignedTo: null,
    displayName: "Procedure/Surgical Discharge Notice",
    documentLabel: "{{ dateOfEvent }} Day Surgery DC - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.NOTIFICATION_HOSPITAL_SDS_DISCHARGE,
    group: "vps",
  },
  [Classification.PHARMACY_RELATED_ALERT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Rx Therapeutic Alert",
    documentLabel: "{{ dateReceived }} Rx Therapeutic Alert - {{ patientInitials }}",
    folder: "Medications VPS",
    folderMapping: "b7eb3f0d-c98d-4e3b-a55b-5e9c476ded58",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PHARMACY_RELATED_ALERT,
    group: "vps",
  },
  [Classification.PHARMACY_RELATED_CLARIFICATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} rx clarification {{ medicationName }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Rx Change/Clarification Request",
    documentLabel: "{{ dateReceived }} Rx Clarification {{ medicationName }} - {{ patientInitials }}",
    folder: "Medications VPS",
    folderMapping: "b7eb3f0d-c98d-4e3b-a55b-5e9c476ded58",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PHARMACY_RELATED_CLARIFICATION,
    group: "vps",
  },
  [Classification.PHARMACY_RELATED_NON_ADHERENCE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Rx non-adherence"],
    assignedTo: null,
    displayName: "Non-adherence",
    documentLabel: "{{ dateReceived }} Rx Non-adherence - {{ patientInitials }}",
    folder: "Medications VPS",
    folderMapping: "b7eb3f0d-c98d-4e3b-a55b-5e9c476ded58",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PHARMACY_RELATED_NON_ADHERENCE,
    group: "vps",
  },
  [Classification.PHARMACY_RELATED_REFILL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} rx refill request {{ medicationName }} sent to {{ familyName }}"],
    assignedTo: null,
    displayName: "Rx Refill Request",
    documentLabel: "{{ dateReceived }} Rx Refill {{ medicationName }} - {{ patientInitials }}",
    folder: "Medications VPS",
    folderMapping: "b7eb3f0d-c98d-4e3b-a55b-5e9c476ded58",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PHARMACY_RELATED_REFILL,
    group: "vps",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} PA Request {{ medicationName }} sent to {{ familyName }} & Brittany"],
    assignedTo: null,
    displayName: "Rx PA request",
    documentLabel: "{{ dateReceived }} {{ medicationName }} PA Request - {{ patientInitials }}",
    folder: "Medications VPS",
    folderMapping: "b7eb3f0d-c98d-4e3b-a55b-5e9c476ded58",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    group: "vps",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "{{ companyName }} PA {{ authorizationStatus }} {{ medicationName }} sent to {{ familyName }} & Brittany",
    ],
    assignedTo: null,
    displayName: "Rx PA response",
    documentLabel: "{{ dateReceived }} {{ medicationName }} PA {{ authorizationStatus }} - {{ patientInitials }}",
    folder: "Medications VPS",
    folderMapping: "b7eb3f0d-c98d-4e3b-a55b-5e9c476ded58",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    group: "vps",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} Procedure PA Request sent to Renee"],
    assignedTo: null,
    displayName: "Procedure PA Request",
    documentLabel: "{{ dateReceived }} Procedure PA Request - {{ patientInitials }}",
    folder: "Other VPS",
    folderMapping: "d40a7a1a-8f4d-4125-bce4-226cc227105f",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    group: "vps",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ companyName }} Procedure PA {{ authorizationStatus }} sent to Renee"],
    assignedTo: null,
    displayName: "Procedure PA response",
    documentLabel: "{{ dateReceived }} Procedure PA {{ authorizationStatus }} - {{ patientInitials }}",
    folder: "Other VPS",
    folderMapping: "d40a7a1a-8f4d-4125-bce4-226cc227105f",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Email + Tel encounter + NextGen upload",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_ER_REPORT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} ER report"],
    assignedTo: null,
    displayName: "progress_note_er_report",
    documentLabel: "{{ dateOfEvent }} {{ clinicName }} ER Report - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_ER_REPORT,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_ER_SUMMARY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} ER summary of care"],
    assignedTo: null,
    displayName: "Summary of Care",
    documentLabel: "{{ dateOfEvent }} {{ clinicName }} ER Summary - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_ER_SUMMARY,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} discharge summary"],
    assignedTo: null,
    displayName: "Discharge Summary",
    documentLabel: "{{ dateOfEvent }} {{ clinicName }} DC Summary - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_RECORDS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} Medical records"],
    assignedTo: null,
    displayName: "progress_note_hospital_records",
    documentLabel: "{{ dateOfEvent }} {{ clinicName }} Hospital Records - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} Hospital summary of care"],
    assignedTo: null,
    displayName: "Summary of Care",
    documentLabel: "{{ dateOfEvent }} {{ clinicName }} Hospital Summary - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_OFFICE_VISIT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} Medical records"],
    assignedTo: null,
    displayName: "Medical records from MD/hospital",
    documentLabel: "{{ dateOfEvent }} {{ specialtyFolder }} Progress Note - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_OFFICE_VISIT,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} Op report"],
    assignedTo: null,
    displayName: "progress_note_operative_report",
    documentLabel: "{{ dateOfEvent }} {{ clinicName }} Op Report - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_PT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ dateOfEvent }} recvd {{ companyName }}"],
    assignedTo: null,
    displayName: "Physical Therapy POC - No Sig/Action Required",
    documentLabel: "{{ companyName }}_{{ dateOfEvent }}_{{ patientInitials }}",
    folder: "External Medical Records",
    folderMapping: "3be3f736-3fcc-4f2b-9849-550d456c2899",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_PT,
    group: "vps",
  },
  [Classification.PROGRESS_NOTE_URGENT_CARE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["{{ clinicName }} Urgent care records"],
    assignedTo: null,
    displayName: "progress_note_urgent_care",
    documentLabel: "{{ dateOfEvent }} {{ companyName }} Urgent Care - {{ patientInitials }}",
    folder: "MD/Hospital Records",
    folderMapping: "70e2af7b-4264-46e8-b6b0-5557fcbfd3f0",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Tel encounter + NextGen upload",
    classification: Classification.PROGRESS_NOTE_URGENT_CARE,
    group: "vps",
  },
  [Classification.REFERRAL_OUTSIDE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["FALSE"],
    assignedTo: null,
    displayName: "New patient (intakes/referrals)",
    documentLabel: "{{ dateReceived }} Referral - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Email only",
    classification: Classification.REFERRAL_OUTSIDE,
    group: "vps",
  },
} as const;
